import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import {
  Unlock, MapPin, Phone, Mail, Clock, User, Loader2, FileText, ChevronDown, ChevronUp, Trophy, Flag, X, AlertCircle, MessageCircle, Image as ImageIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Build a WhatsApp deeplink for South African numbers.
// Handles "+27 82 555 9876", "0825559876", "27825559876", with or without spaces/dashes.
// Returns null if the number doesn't look like a usable SA mobile.
const buildWhatsAppLink = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return null;
  let normalised = digits;
  if (normalised.startsWith('00')) normalised = normalised.slice(2);
  if (normalised.startsWith('0') && normalised.length === 10) {
    normalised = '27' + normalised.slice(1);
  }
  if (!normalised.startsWith('27')) {
    // Allow international numbers too — just pass through if length looks valid
    if (normalised.length < 9 || normalised.length > 15) return null;
  } else if (normalised.length !== 11) {
    return null;
  }
  return `https://wa.me/${normalised}`;
};

const REPORT_REASONS = [
  { value: 'invalid_phone', label: 'Invalid phone number' },
  { value: 'duplicate', label: 'Duplicate lead' },
  { value: 'unreachable', label: 'Customer unreachable' },
  { value: 'wrong_service', label: 'Wrong service category' },
  { value: 'spam', label: 'Spam / fake request' },
];

const REPORT_STATUS_BADGE = {
  pending: { label: 'Report under review', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Report approved — credits refunded', className: 'bg-green-50 text-green-700 border-green-200' },
  rejected: { label: 'Report rejected', className: 'bg-gray-50 text-gray-600 border-gray-200' },
};

const MyLeads = () => {
  const { proId } = useOutletContext();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLead, setExpandedLead] = useState(null);
  const [markingWon, setMarkingWon] = useState(null);
  const [reports, setReports] = useState({}); // keyed by lead_id (= service_request_id)
  const [reportModalLead, setReportModalLead] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportNotes, setReportNotes] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [leadsRes, repsRes] = await Promise.all([
          axios.get(`${API}/dashboard/my-leads/${proId}`),
          axios.get(`${API}/leads/reports/${proId}`),
        ]);
        setLeads(leadsRes.data);
        const idx = {};
        (repsRes.data || []).forEach(r => { idx[r.lead_id] = r; });
        setReports(idx);
      } catch (e) {
        console.error('Error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [proId]);

  const handleMarkAsWon = async (lead) => {
    if (!lead.service_id) {
      alert('Service ID not found. Cannot update status.');
      return;
    }

    // Determine the new state (toggle)
    const newIsWinner = !lead.is_winner;
    
    // Check toggle limit
    if (lead.toggle_count >= 3) {
      alert('❌ Toggle limit reached! You cannot change the status of this lead anymore (max 3 changes).');
      return;
    }

    setMarkingWon(lead.id);
    try {
      const response = await axios.post(`${API}/professionals/${proId}/mark-job-won`, {
        service_id: lead.service_id,
        service_request_id: lead.service_request_id,
        is_winner: newIsWinner
      });

      // Update local state
      setLeads(prevLeads =>
        prevLeads.map(l =>
          l.id === lead.id ? { 
            ...l, 
            is_winner: newIsWinner,
            toggle_count: response.data.toggle_count,
            updated_at: new Date().toISOString()
          } : l
        )
      );

      const togglesRemaining = response.data.toggles_remaining;
      const statusMsg = newIsWinner ? 'won' : 'not won';
      let message = `✅ Lead marked as ${statusMsg}!`;
      
      if (togglesRemaining === 0) {
        message += '\n⚠️ This was your last toggle for this lead.';
      } else if (togglesRemaining === 1) {
        message += `\n⚠️ You have ${togglesRemaining} toggle remaining.`;
      } else {
        message += `\nYou have ${togglesRemaining} toggles remaining.`;
      }
      
      alert(message);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update status';
      alert(`❌ ${msg}`);
      console.error('Error updating status:', err);
    } finally {
      setMarkingWon(null);
    }
  };

  const openReportModal = (lead) => {
    setReportModalLead(lead);
    setReportReason('');
    setReportNotes('');
    setReportError('');
  };

  const closeReportModal = () => {
    setReportModalLead(null);
    setReportError('');
  };

  const submitReport = async () => {
    if (!reportReason) {
      setReportError('Please select a reason.');
      return;
    }
    setReportSubmitting(true);
    setReportError('');
    try {
      const res = await axios.post(`${API}/leads/report-issue`, {
        professional_id: proId,
        lead_id: reportModalLead.service_request_id,
        reason: reportReason,
        notes: reportNotes,
      });
      setReports(prev => ({
        ...prev,
        [reportModalLead.service_request_id]: {
          lead_id: reportModalLead.service_request_id,
          professional_id: proId,
          reason: reportReason,
          notes: reportNotes,
          status: res.data.status || 'pending',
        },
      }));
      closeReportModal();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setReportError(typeof detail === 'string' ? detail : 'Failed to submit report. Please try again.');
    } finally {
      setReportSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Leads</h1>
        <p className="text-sm text-gray-500 mt-1">
          Leads you've unlocked with full customer contact details.
        </p>
      </div>

      {leads.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Unlock size={40} className="text-gray-300 mx-auto mb-4" />
          <h3 className="text-base font-semibold text-gray-900 mb-1">No unlocked leads yet</h3>
          <p className="text-sm text-gray-500">Unlock leads from the Available Leads page to see full details here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map((lead, idx) => {
            const isExpanded = expandedLead === idx;
            return (
              <div key={lead.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <h3 className="text-base font-bold text-gray-900">{lead.service_name}</h3>
                      {lead.description && (
                        <p className="text-sm text-gray-500 mt-1">{lead.description}</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {lead.unlocked_at ? new Date(lead.unlocked_at).toLocaleDateString() : ''}
                    </span>
                  </div>

                  {/* Customer Contact */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2.5">
                    <div className="flex items-center gap-3">
                      <User size={16} className="text-gray-400 flex-shrink-0" />
                      <span className="text-sm font-semibold text-gray-900">{lead.customer_name || 'Not provided'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail size={16} className="text-gray-400 flex-shrink-0" />
                      <a href={`mailto:${lead.customer_email}`} className="text-sm text-[#37b34a] hover:underline">
                        {lead.customer_email || 'Not provided'}
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone size={16} className="text-gray-400 flex-shrink-0" />
                      <a href={`tel:${lead.customer_phone}`} className="text-sm text-[#37b34a] hover:underline">
                        {lead.customer_phone || 'Not provided'}
                      </a>
                      {(() => {
                        const waLink = buildWhatsAppLink(lead.customer_phone);
                        if (!waLink) return null;
                        return (
                          <a
                            href={waLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`whatsapp-btn-${lead.id}`}
                            title="Open in WhatsApp"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold text-[#25D366] bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors"
                          >
                            <MessageCircle size={12} />
                            WhatsApp
                          </a>
                        );
                      })()}
                    </div>
                    {lead.location && (
                      <div className="flex items-center gap-3">
                        <MapPin size={16} className="text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{lead.location}</span>
                      </div>
                    )}
                  </div>

                  {/* Customer-provided photos */}
                  {Array.isArray(lead.images) && lead.images.length > 0 && (
                    <div className="mt-3" data-testid={`lead-images-${lead.id}`}>
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                        <ImageIcon size={12} />
                        Customer photos
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {lead.images.map((src, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setLightboxImage(src)}
                            data-testid={`lead-image-thumb-${lead.id}-${i}`}
                            className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 hover:border-[#37b34a] transition-colors"
                            aria-label={`View photo ${i + 1}`}
                          >
                            <img
                              src={src}
                              alt={`Lead photo ${i + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expandable Answers */}
                  {lead.answers && Object.keys(lead.answers).length > 0 && (
                    <div className="mt-3">
                      <button
                        onClick={() => setExpandedLead(isExpanded ? null : idx)}
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
                      >
                        <FileText size={14} />
                        {isExpanded ? 'Hide' : 'View'} questionnaire answers
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      {isExpanded && (
                        <div className="mt-3 bg-blue-50/50 rounded-lg p-4 space-y-2">
                          {Object.entries(lead.answers).map(([q, a], i) => (
                            <div key={i}>
                              <p className="text-xs font-semibold text-gray-500">{q}</p>
                              <p className="text-sm text-gray-800">{Array.isArray(a) ? a.join(', ') : String(a)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Meta */}
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> Credits used: {lead.credits_used}
                    </span>
                    {lead.budget_range && <span>Budget: {lead.budget_range}</span>}
                    <span>Urgency: {lead.urgency_level}</span>
                  </div>

                  {/* Toggle Winner Status Button */}
                  <div className="mt-4">
                    {lead.toggle_count >= 3 ? (
                      // Toggle limit reached - show final state only
                      <div className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-semibold text-sm border ${
                        lead.is_winner 
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}>
                        <Trophy size={16} />
                        {lead.is_winner ? 'Job Won ✓' : 'Not Won'}
                        <span className="text-xs opacity-70">(Toggle limit reached)</span>
                      </div>
                    ) : (
                      // Can still toggle
                      <button
                        onClick={() => handleMarkAsWon(lead)}
                        disabled={markingWon === lead.id}
                        data-testid={`toggle-winner-btn-${lead.id}`}
                        className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                          lead.is_winner
                            ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                            : 'bg-gradient-to-r from-[#37b34a] to-[#2d9c3d] text-white hover:from-[#2d9c3d] hover:to-[#248632]'
                        }`}
                      >
                        {markingWon === lead.id ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <Trophy size={16} />
                            {lead.is_winner ? 'Mark as Not Won' : 'Mark as Won'}
                            {lead.toggle_count > 0 && (
                              <span className="text-xs opacity-70">
                                ({3 - lead.toggle_count} left)
                              </span>
                            )}
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Report Issue with Lead */}
                  {(() => {
                    const existingReport = reports[lead.service_request_id];
                    if (existingReport) {
                      const badge = REPORT_STATUS_BADGE[existingReport.status] || REPORT_STATUS_BADGE.pending;
                      return (
                        <div
                          data-testid={`lead-report-badge-${lead.id}`}
                          className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border ${badge.className}`}
                        >
                          <Flag size={12} />
                          {badge.label}
                        </div>
                      );
                    }
                    return (
                      <button
                        onClick={() => openReportModal(lead)}
                        data-testid={`report-lead-btn-${lead.id}`}
                        className="mt-3 w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 hover:text-red-600 border border-gray-200 transition-colors"
                      >
                        <Flag size={12} />
                        Report Issue with Lead
                      </button>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Image lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-4 cursor-zoom-out"
          onClick={() => setLightboxImage(null)}
          data-testid="lead-image-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Close image"
          >
            <X size={18} />
          </button>
          <img
            src={lightboxImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Report Issue Modal */}
      {reportModalLead && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={closeReportModal}
          data-testid="report-modal"
        >
          <div
            className="bg-white rounded-2xl border border-gray-200 w-full max-w-md p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Report Issue with Lead</h2>
                <p className="text-xs text-gray-500 mt-0.5">{reportModalLead.service_name}</p>
              </div>
              <button
                onClick={closeReportModal}
                className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
                data-testid="report-modal-close"
              >
                <X size={18} />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              If something is wrong with this lead, let us know. Approved reports will refund the credits you spent.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
              <div className="space-y-2" data-testid="report-reason-list">
                {REPORT_REASONS.map(r => (
                  <label
                    key={r.value}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                      reportReason === r.value
                        ? 'border-[#37b34a] bg-[#37b34a]/5'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="report-reason"
                      value={r.value}
                      checked={reportReason === r.value}
                      onChange={() => setReportReason(r.value)}
                      data-testid={`report-reason-${r.value}`}
                      className="accent-[#37b34a]"
                    />
                    <span className="text-sm text-gray-800">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Notes <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={reportNotes}
                onChange={(e) => setReportNotes(e.target.value)}
                rows={3}
                maxLength={500}
                data-testid="report-notes-input"
                placeholder="Anything else you'd like our team to know?"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#37b34a] resize-none"
              />
            </div>

            {reportError && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg mb-3">
                <AlertCircle size={14} />
                {reportError}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={closeReportModal}
                className="flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitReport}
                disabled={reportSubmitting || !reportReason}
                data-testid="report-submit-btn"
                className="flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold text-white bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 transition-colors flex items-center justify-center gap-2"
              >
                {reportSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Flag size={14} />}
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyLeads;
